import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Pagination } from '../../../model/paginatiion';
import { IBiddingResponse } from '../../../model/base/response/bidding-response';
import { IModalityBiddingResponse } from '../../../model/base/response/modality-bidding-response';

interface BiddingState {
  biddings: Pagination<IBiddingResponse>;
  modalities: IModalityBiddingResponse[];
  loading: boolean;
  loadingFilters: boolean;
  error: string | null;
}

type BiddingAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: Pagination<IBiddingResponse> }
  | { type: 'LOAD_ERROR'; payload: string }
  | { type: 'LOAD_MODALITIES_START' }
  | { type: 'LOAD_MODALITIES_SUCCESS'; payload: IModalityBiddingResponse[] }
  | { type: 'LOAD_MODALITIES_ERROR'; payload: string };

const initialState: BiddingState = {
  biddings: new Pagination<IBiddingResponse>(),
  modalities: [],
  loading: false,
  loadingFilters: false,
  error: null,
};

const BiddingStateContext = createContext<BiddingState | undefined>(undefined);
const BiddingDispatchContext = createContext<React.Dispatch<BiddingAction> | undefined>(undefined);

function biddingReducer(state: BiddingState, action: BiddingAction): BiddingState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, biddings: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'LOAD_MODALITIES_START':
      return { ...state, loadingFilters: true };
    case 'LOAD_MODALITIES_SUCCESS':
      return { ...state, loadingFilters: false, modalities: action.payload };
    case 'LOAD_MODALITIES_ERROR':
      return { ...state, loadingFilters: false, error: action.payload };
    default:
      return state;
  }
}

export function BiddingProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(biddingReducer, initialState);

  return (
    <BiddingStateContext.Provider value={state}>
      <BiddingDispatchContext.Provider value={dispatch}>
        {children}
      </BiddingDispatchContext.Provider>
    </BiddingStateContext.Provider>
  );
}

export function useBiddingState() {
  const context = useContext(BiddingStateContext);
  if (context === undefined) {
    throw new Error('useBiddingState must be used within a BiddingProvider');
  }
  return context;
}

export function useBiddingDispatch() {
  const context = useContext(BiddingDispatchContext);
  if (context === undefined) {
    throw new Error('useBiddingDispatch must be used within a BiddingProvider');
  }
  return context;
}
